import React from 'react';

/**
 * NuriCompanion — The Nuri leaf character with 6 expression states.
 *
 * States: happy, neutral, thinking, empathetic, excited, sleeping
 * Sizes: sm (48px), md (80px), lg (120px)
 */

const SIZES = { sm: 48, md: 80, lg: 120 };

export function NuriCompanion({ state = 'neutral', size = 'sm', className = '' }) {
  const s = SIZES[size] || SIZES.sm;
  const eyeY = s * 0.38;
  const eyeSpacing = s * 0.12;
  const cx = s / 2;
  const eyeR = s * 0.03;
  const mouthY = s * 0.52;

  const animations = {
    happy: 'nuri-bounce',
    neutral: 'nuri-breathe',
    thinking: 'nuri-tilt',
    empathetic: 'nuri-sway',
    excited: 'nuri-bounce-big',
    sleeping: 'nuri-breathe-slow',
  };

  const anim = animations[state] || animations.neutral;

  return (
    <div className={`inline-flex items-center justify-center ${className}`}
      style={{ width: s, height: s }}
      data-testid={`nuri-companion-${state}`}>
      <style>{`
        @keyframes nuri-breathe { 0%,100%{transform:scale(1)} 50%{transform:scale(1.03)} }
        @keyframes nuri-breathe-slow { 0%,100%{transform:scale(1)} 50%{transform:scale(1.02)} }
        @keyframes nuri-bounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-3px)} }
        @keyframes nuri-bounce-big { 0%,100%{transform:translateY(0) scale(1)} 25%{transform:translateY(-5px) scale(1.05)} 50%{transform:translateY(0) scale(1)} 75%{transform:translateY(-3px) scale(1.02)} }
        @keyframes nuri-tilt { 0%,100%{transform:rotate(0)} 50%{transform:rotate(4deg)} }
        @keyframes nuri-sway { 0%,100%{transform:translateX(0)} 50%{transform:translateX(2px)} }
        @keyframes nuri-blink { 0%,90%,100%{ry:1} 95%{ry:0.2} }
      `}</style>
      <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}
        style={{ animation: `${anim} ${state === 'excited' ? '0.6s' : state === 'sleeping' ? '4s' : '2.5s'} ease-in-out infinite` }}>

        {/* Leaf body */}
        <path
          d={`M${cx} ${s * 0.12} C${s * 0.18} ${s * 0.2}, ${s * 0.1} ${s * 0.55}, ${s * 0.15} ${s * 0.72} C${s * 0.2} ${s * 0.85}, ${s * 0.35} ${s * 0.92}, ${cx} ${s * 0.88} C${s * 0.65} ${s * 0.92}, ${s * 0.8} ${s * 0.85}, ${s * 0.85} ${s * 0.72} C${s * 0.9} ${s * 0.55}, ${s * 0.82} ${s * 0.2}, ${cx} ${s * 0.12}`}
          fill="#C67A5C"
          stroke="#B06A4C"
          strokeWidth={s * 0.015}
        />

        {/* Inner highlight */}
        <path
          d={`M${cx} ${s * 0.18} C${s * 0.24} ${s * 0.25}, ${s * 0.16} ${s * 0.5}, ${s * 0.2} ${s * 0.65} C${s * 0.25} ${s * 0.78}, ${s * 0.38} ${s * 0.84}, ${cx} ${s * 0.82}`}
          fill="none"
          stroke="rgba(255,255,255,0.15)"
          strokeWidth={s * 0.02}
          strokeLinecap="round"
        />

        {/* Leaf vein */}
        <line x1={cx} y1={s * 0.2} x2={cx} y2={s * 0.75}
          stroke="rgba(255,255,255,0.1)" strokeWidth={s * 0.012} strokeLinecap="round" />

        {/* Eyes */}
        {state === 'sleeping' ? (
          <>
            <line x1={cx - eyeSpacing - eyeR * 2} y1={eyeY} x2={cx - eyeSpacing + eyeR * 2} y2={eyeY}
              stroke="#FDFBF7" strokeWidth={s * 0.02} strokeLinecap="round" />
            <line x1={cx + eyeSpacing - eyeR * 2} y1={eyeY} x2={cx + eyeSpacing + eyeR * 2} y2={eyeY}
              stroke="#FDFBF7" strokeWidth={s * 0.02} strokeLinecap="round" />
          </>
        ) : (
          <>
            <ellipse cx={cx - eyeSpacing} cy={eyeY} rx={eyeR} ry={eyeR}
              fill="#FDFBF7" style={{ animation: 'nuri-blink 3s ease infinite' }} />
            <ellipse cx={cx + eyeSpacing} cy={eyeY} rx={eyeR} ry={eyeR}
              fill="#FDFBF7" style={{ animation: 'nuri-blink 3s ease infinite 0.1s' }} />
          </>
        )}

        {/* Mouth */}
        {(state === 'happy' || state === 'excited') && (
          <path
            d={`M${cx - s * 0.06} ${mouthY} Q${cx} ${mouthY + s * 0.05} ${cx + s * 0.06} ${mouthY}`}
            fill="none" stroke="#FDFBF7" strokeWidth={s * 0.015} strokeLinecap="round" />
        )}
        {state === 'neutral' && (
          <line x1={cx - s * 0.04} y1={mouthY} x2={cx + s * 0.04} y2={mouthY}
            stroke="#FDFBF7" strokeWidth={s * 0.012} strokeLinecap="round" opacity="0.7" />
        )}
        {state === 'empathetic' && (
          <path
            d={`M${cx - s * 0.05} ${mouthY + s * 0.01} Q${cx} ${mouthY - s * 0.01} ${cx + s * 0.05} ${mouthY + s * 0.01}`}
            fill="none" stroke="#FDFBF7" strokeWidth={s * 0.012} strokeLinecap="round" opacity="0.7" />
        )}
        {state === 'thinking' && (
          <>
            <line x1={cx - s * 0.03} y1={mouthY} x2={cx + s * 0.02} y2={mouthY + s * 0.01}
              stroke="#FDFBF7" strokeWidth={s * 0.012} strokeLinecap="round" opacity="0.6" />
            {/* Thinking dots */}
            <circle cx={cx + s * 0.2} cy={eyeY - s * 0.06} r={s * 0.015} fill="#FDFBF7" opacity="0.4">
              <animate attributeName="opacity" values="0.2;0.6;0.2" dur="1.5s" repeatCount="indefinite" />
            </circle>
            <circle cx={cx + s * 0.26} cy={eyeY - s * 0.1} r={s * 0.012} fill="#FDFBF7" opacity="0.3">
              <animate attributeName="opacity" values="0.1;0.4;0.1" dur="1.5s" begin="0.3s" repeatCount="indefinite" />
            </circle>
          </>
        )}
        {state === 'sleeping' && (
          <>
            {/* Zzz */}
            <text x={cx + s * 0.18} y={eyeY - s * 0.08} fill="#FDFBF7" opacity="0.3"
              fontSize={s * 0.08} fontFamily="var(--font-sans)" fontWeight="600">
              <animate attributeName="opacity" values="0.1;0.4;0.1" dur="2s" repeatCount="indefinite" />
              z
            </text>
            <text x={cx + s * 0.24} y={eyeY - s * 0.14} fill="#FDFBF7" opacity="0.2"
              fontSize={s * 0.06} fontFamily="var(--font-sans)" fontWeight="600">
              <animate attributeName="opacity" values="0;0.3;0" dur="2s" begin="0.5s" repeatCount="indefinite" />
              z
            </text>
          </>
        )}
      </svg>
    </div>
  );
}
